import React, { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Toolbar,
  Hidden,
  IconButton,
  Box,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Logo from "../../Logo";
import {
  TopNavMessages,
  TopNavAvatar,
  TopNavAppViewDropdown,
  TopNavNotifications,
  TopNavProfileDropdown
} from ".";
import { withRouter } from "react-router";
import { isIframed } from "../../../utils/iframe";

const useStyles = makeStyles(theme => ({
  appBar: {
    height: theme.sizes.topNavBarHeight,
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.secondary,
    boxShadow: "0px 7px 10px -10px rgba(0,0,0,0.3)"
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      display: "none"
    }
  },
  logoContainer: {
    minWidth: 108,
    width: 108,
    marginRight: theme.spacing(3),
    display: "flex"
  },
  title: {
    marginLeft: theme.spacing(3),
    whiteSpace: "nowrap",
    alignSelf: "center"
  }
}));

const TopNav = withRouter(props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [messagesMenuAnchor, setMessagesMenuAncor] = useState(null);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);

  const {
    logo,
    logoStyle,
    handleMenuToggle,
    numNotifications,
    firstName,
    lastName,
    avatarUrl,
    learningCenterCaption,
    currentUser,
    unreadMessages,
    profileDropdownOptions,
    onProfileDropdownOptionClick,
    location
  } = props;

  /**
   * Called when the user clicks outside of the messages/notifications box to expand or
   * collapse the messages/notifications
   */
  const handleExpandNotifications = event => {
    setMessagesMenuAncor(event.currentTarget);
    setNotificationsExpanded(true);
  };

  /**
   * called when the user clicks outside of the notifications to hide
   * the messages box
   */
  const handleLeaveMessages = () => {
    setMessagesMenuAncor(null);
    setNotificationsExpanded(false);
  };

  if (isIframed) {
    return null;
  }

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar>
        <Hidden smDown implementation="css">
          <Box display="flex" position="relative" pl={1}>
            {logoStyle && logoStyle.includes("logo") && (
              <div className={classes.logoContainer}>
                <Logo logo={logo} />
              </div>
            )}
            {logoStyle === "logo_and_text" && (
              <div>
                <div className="vertical-divider" />
              </div>
            )}
            {logoStyle && logoStyle.includes("text") && (
              <Typography className={classes.title} variant="subtitle2">
                {learningCenterCaption}
              </Typography>
            )}
          </Box>
        </Hidden>
        <Hidden smUp>
          <IconButton
            aria-label="Open Menu"
            edge="start"
            onClick={handleMenuToggle}
            className={classes.menuButton}
            size="large"
          >
            <MenuIcon />
          </IconButton>
        </Hidden>
        <Box display="flex" flexDirection="column" width="100%">
          <Box
            data-qa="top-nav"
            display="flex"
            alignItems="center"
            height="100%"
            justifyContent="flex-end"
          >
            <TopNavAppViewDropdown
              currentPath={location.pathname}
              userApps={currentUser ? currentUser.apps : null}
            />
            <Box pr={{ xs: 0, sm: 3 }} ml={{ xs: 1, sm: 4 }}>
              <>
                <TopNavNotifications
                  onExpandNotifications={handleExpandNotifications}
                  numNotifications={numNotifications}
                  active={notificationsExpanded}
                />
              </>
            </Box>
            <TopNavAvatar
              imageSrc={avatarUrl}
              userFirstName={firstName}
              userLastName={lastName}
              welcomeMessage={
                <Typography variant="subtitle2">
                  {t("learningCenterTopNav.welcome")}, {firstName} {lastName}
                </Typography>
              }
            />
            <TopNavProfileDropdown
              profileDropdownOptions={profileDropdownOptions}
              onOptionClick={onProfileDropdownOptionClick}
            />
            <TopNavMessages
              messages={unreadMessages}
              messagesMenuAnchor={messagesMenuAnchor}
              messagesExpanded={notificationsExpanded}
              onLeaveMessages={handleLeaveMessages}
            />
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
});

TopNav.propTypes = {
  logo: PropTypes.string,
  logoStyle: PropTypes.string,
  handleMenuToggle: PropTypes.func,
  numNotifications: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  avatarUrl: PropTypes.string,
  learningCenterCaption: PropTypes.string,
  currentUser: PropTypes.object,
  currentPath: PropTypes.string,
  unreadMessages: PropTypes.array
};

export default TopNav;
